import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import appCache from './AppCache';

export interface UserModules {
  email: boolean;
  campaign: boolean;
  crm: boolean;
  schedule: boolean;
  calendar: boolean;
  aiDocuments: boolean;
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

const COLD_START_MODULES: UserModules = {
  email: true,
  campaign: true,
  crm: true,
  schedule: true,
  calendar: true,
  aiDocuments: false,
};

let cachedModules: UserModules | null = null;
let inFlightFetch: Promise<UserModules> | null = null;

type RawUserModules = Partial<UserModules> & { AiDocuments?: boolean };

export function normalizeUserModules(d: RawUserModules | null | undefined): UserModules {
  const raw = d as RawUserModules | null | undefined;
  return {
    email: raw?.email !== false,
    campaign: raw?.campaign !== false,
    crm: raw?.crm !== false,
    schedule: raw?.schedule !== false,
    calendar: raw?.calendar !== false,
    aiDocuments: raw?.aiDocuments === true || raw?.AiDocuments === true,
  };
}

const MODULES_CACHE_TTL_MS = 5 * 60 * 1000;

function readCachedModules(): UserModules | null {
  if (cachedModules) return cachedModules;
  const fromAppCache = appCache.get<UserModules>('modules');
  if (fromAppCache) {
    cachedModules = normalizeUserModules(fromAppCache);
    return cachedModules;
  }
  return null;
}

function writeModulesCache(modules: UserModules): UserModules {
  cachedModules = modules;
  appCache.set('modules', modules, MODULES_CACHE_TTL_MS);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('modulesChanged', { detail: modules }));
  }
  return modules;
}

/** Seeds in-memory + app cache (e.g. login bootstrap) without a network round-trip. */
export function seedModulesCache(modules: UserModules): UserModules {
  const normalized = normalizeUserModules(modules);
  cachedModules = normalized;
  appCache.set('modules', normalized, MODULES_CACHE_TTL_MS);
  return normalized;
}

async function fetchModulesFromApi(): Promise<UserModules> {
  if (inFlightFetch) return inFlightFetch;

  inFlightFetch = (async () => {
    const response = await axios.get<RawUserModules>(`${API_URL}/users/modules`, {
      headers: getAuthHeaders(),
      params: { fresh: true, _t: Date.now() },
    });
    return writeModulesCache(normalizeUserModules(response.data));
  })();

  try {
    return await inFlightFetch;
  } finally {
    inFlightFetch = null;
  }
}

export const getUserModules = async (forceRefresh = false): Promise<UserModules> => {
  if (!SessionService.isAuthenticated()) {
    return COLD_START_MODULES;
  }

  if (!forceRefresh) {
    const cached = readCachedModules();
    if (cached) return cached;
  }

  try {
    return await fetchModulesFromApi();
  } catch {
    return readCachedModules() ?? COLD_START_MODULES;
  }
};

export const clearModulesCache = () => {
  cachedModules = null;
  inFlightFetch = null;
  appCache.invalidate('modules');
};
